import React, { useCallback, useEffect, useMemo, useState } from 'react';

import {
  CryptoFormatter,
  IntlManager,
  NotificationManager,
  prepareRequest,
  SiteConfigManager,
  useSettings,
} from '@atlas/core';
import { parseWithdrawalQuery } from '@atlas/routing';
import { Button, Input, Label, RadioSelect, Select } from '@atlas/ui';

import { NotificationEmitterDefaultPopup } from 'common/Popup/NotificationEmitterDefaultPopup';
import PaymentListItem from 'components/ClientProfile/Payment/PaymentListItem';

import { WithdrawalBitcoinPopupInfoLabel } from './WithdrawalBitcoinPopupInfoLabel';

const optionsRadioSelect = [{ value: true }, { value: false }];

export const WithdrawalBitcoinPopup = ({ data: { paymentMethod, redirectUrl }, id }) => {
  const {
    addresses,
    amount,
    currencyFrom,
    currencyTo,
    exchangeAmount,
    exchangeRate,
    extTxId,
    feeAmount,
    feeCurrency,
    pmName,
  } = parseWithdrawalQuery(redirectUrl) || {};
  const { lang } = useSettings();
  const languageCode = useMemo(() => {
    const currentLang = SiteConfigManager.getConfig('languages')?.find(
      language => language.code === lang,
    );
    return currentLang?.hreflang || currentLang?.alpha3Code;
  }, [lang]);

  const [isSavedAddress, setIsSavedAddress] = useState(true);
  const [savedAddress, setSavedAddress] = useState();
  const [newAddress, setNewAddress] = useState();
  const [isActiveSelect, setIsActiveSelect] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const activatedSelect = useCallback(() => setIsActiveSelect(true), []);
  const deactivatedSelect = useCallback(() => setIsActiveSelect(false), []);

  const address = useMemo(
    () => (addresses && isSavedAddress ? savedAddress : newAddress),
    [addresses, isSavedAddress, savedAddress, newAddress],
  );
  const inputLabel = useMemo(() => (addresses ? 'new_address' : 'address'), [addresses]);
  const optionsSelect = useMemo(() => {
    const addressesArray = Array.isArray(addresses) ? addresses : [addresses];
    return addressesArray?.map(item => ({ text: item, value: item }));
  }, [addresses]);

  const handleClose = useCallback(() => NotificationManager.removeNotification(id), [id]);
  const handleChangeAddresses = useCallback(option => setIsSavedAddress(option.value), []);
  const handleChangeInput = useCallback(data => setNewAddress(data.target.value.trim()), []);

  const handleClickButton = useCallback(async () => {
    if (isSubmitting) return;
    const request = prepareRequest({
      method: 'POST',
      url: 'payments/coinspaid/withdraw',
    });

    setIsSubmitting(true);

    try {
      const response = await request({
        amount,
        cardToken: address,
        cryptoCurrency: currencyTo,
        extPaymentId: extTxId,
        fiatCurrency: currencyFrom,
        pmName,
      });
      NotificationManager.display({
        data: {
          amount,
          currency: currencyFrom,
          publicPaymentId: response?.publicPaymentId,
          paymentMethod,
        },
        message: 'successfulWithdrawal',
        type: 'successfulWithdrawal',
      });
    } catch (error) {
      NotificationManager.notify({
        message: 'withdrawal_error_title',
        status: 'error',
        type: 'notification',
      });
    } finally {
      handleClose();
    }
  }, [
    isSubmitting,
    amount,
    address,
    currencyTo,
    extTxId,
    currencyFrom,
    pmName,
    paymentMethod,
    handleClose,
  ]);

  useEffect(
    () => () => {
      setIsSavedAddress(true);
      setSavedAddress(null);
      setNewAddress(null);
    },
    [],
  );

  const currencyToFormatter = useMemo(
    () => new CryptoFormatter(languageCode, currencyTo),
    [currencyTo, languageCode],
  );

  const feeFormatter = useMemo(
    () => new CryptoFormatter(languageCode, feeCurrency),
    [feeCurrency, languageCode],
  );

  return (
    <NotificationEmitterDefaultPopup
      headerTitle="withdrawal"
      headerTitlePosition="left"
      onClose={handleClose}
    >
      <div className="withdrawal-bitcoin__body">
        <div className="withdrawal-bitcoin__payment-method">
          <PaymentListItem payment={paymentMethod} />
        </div>

        {addresses && (
          <>
            <Label message="select_address" />:
          </>
        )}
        <div className="withdrawal-bitcoin__from-wrapper">
          {addresses && (
            <RadioSelect
              className="withdrawal-bitcoin__radio"
              onChange={handleChangeAddresses}
              options={optionsRadioSelect}
              value={isSavedAddress}
            />
          )}

          <div className="withdrawal-bitcoin__addresses">
            {addresses && (
              <Select
                active={isActiveSelect}
                className="withdrawal-bitcoin__select"
                disabled={addresses && !isSavedAddress}
                label="saved_address"
                onBlur={deactivatedSelect}
                onChange={setSavedAddress}
                onFocus={activatedSelect}
                options={optionsSelect}
              />
            )}

            <Input
              className="withdrawal-bitcoin__input"
              disabled={addresses && isSavedAddress}
              label={inputLabel}
              onChange={handleChangeInput}
              value={newAddress}
            />
          </div>
        </div>

        <div className="withdrawal-bitcoin__info">
          <div>
            <WithdrawalBitcoinPopupInfoLabel label="withdrawal_rate" />
            <span>{`${currencyToFormatter.format(10 ** 9)} = ${IntlManager.formatMoney(exchangeRate * 100)}`}</span>
          </div>

          <div>
            <WithdrawalBitcoinPopupInfoLabel label="amount" />
            <span>{`${IntlManager.formatMoney(amount)} (≈ ${currencyToFormatter.format(exchangeAmount * 10 ** 9)})`}</span>
          </div>

          {Boolean(feeAmount) && !Number.isNaN(feeAmount) && (
            <div>
              <WithdrawalBitcoinPopupInfoLabel label="fee" />
              <span>{`≈ ${feeFormatter.format(feeAmount * 10 ** 9)}`}</span>
            </div>
          )}

          <div className="withdrawal-bitcoin__total">
            <WithdrawalBitcoinPopupInfoLabel label="total_amount" />
            <span>{`≈ ${currencyToFormatter.format((exchangeAmount - feeAmount) * 10 ** 9)}`}</span>
          </div>
        </div>

        <Button
          className="withdrawal-bitcoin__button"
          disabled={!address || isSubmitting}
          label="withdrawal"
          onClick={handleClickButton}
          size="big"
        />
      </div>
    </NotificationEmitterDefaultPopup>
  );
};
